import axios from 'axios';

// Function to get CSRF token from cookies
function getCsrfToken() {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Function to get API base URL
function getApiBaseUrl() {
  // Для локальной разработки используем текущий origin через nginx
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '';
  
  if (isLocalhost) {
    // Используем текущий origin (localhost через nginx на порту 80)
    // Nginx проксирует запросы к Django на порт 8000
    return window.location.origin + '/api';
  }
  
  // Если указан явный URL в env - используем его
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL.endsWith('/') 
      ? process.env.REACT_APP_API_URL.slice(0, -1) 
      : process.env.REACT_APP_API_URL;
    // Убираем /api если есть, потом добавим
    return apiUrl.replace(/\/api\/?$/, '') + '/api';
  }
  
  // Для продакшена используем текущий домен
  return window.location.protocol + '//' + window.location.host + '/api';
}

// Create an axios instance
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let refreshRequest = null;

async function refreshAccessToken() {
  if (!refreshRequest) {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('Missing refresh token');
    }

    refreshRequest = axios
      .post(`${getApiBaseUrl()}/users/auth/token/refresh/`, { refresh: refreshToken })
      .then((response) => {
        const { access, refresh } = response.data || {};
        if (!access) {
          throw new Error('Missing access token in refresh response');
        }
        localStorage.setItem('access_token', access);
        if (refresh) {
          localStorage.setItem('refresh_token', refresh);
        }
        return access;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
}

function clearAuthStorage() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
}

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Add CSRF token to headers
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    // Add auth token if exists (используем access_token для JWT)
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isAuthRefreshRequest = originalRequest?.url?.includes('/users/auth/token/refresh/');

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthRefreshRequest) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        clearAuthStorage();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    console.error('API error:', error.response ? error.response.data : error.message);
    return Promise.reject(error);
  }
);

export default axiosInstance;
